<template>
  <!-- Feature off, credits not configured server-side, or checked and not
       gated → render the chat untouched. -->
  <slot v-if="renderChat" />

  <!-- Still resolving the pre-flight check: show a spinner rather than the chat
       so a user can't start typing into a session that will be refused. If the
       check errored, the store fails open (gated=false → renderChat above). -->
  <div
    v-else-if="!creditsStore.checked"
    class="credit-gate d-flex align-center justify-center pa-8"
  >
    <v-progress-circular indeterminate color="primary" />
  </div>

  <!-- Gated: full-panel contact-sales takeover. -->
  <div
    v-else
    class="credit-gate d-flex flex-column align-center justify-center pa-6 text-center"
  >
    <div class="gate-inner">
      <template v-if="reason === 'no_pool'">
        <img :src="gateIcon" alt="RPilot" class="gate-icon mb-3" />
        <div class="text-h6 mb-2">{{ t('gate.meetTitle') }}</div>
        <div class="text-body-2 text-medium-emphasis mb-5">
          {{ t('gate.meetBody') }}
        </div>
        <div class="text-body-2 font-weight-medium mb-3">
          {{ t('gate.meetCta') }}
        </div>
      </template>

      <template v-else>
        <v-icon size="44" class="mb-3" color="primary">mdi-credit-card-off-outline</v-icon>
        <div class="text-h6 mb-2">{{ t('gate.outOfCreditsTitle') }}</div>
        <div class="text-body-2 text-medium-emphasis mb-5">
          {{ t('gate.outOfCreditsBody') }}
        </div>
      </template>

      <!-- When embedded under snagr-web the parent owns a Freshchat widget —
           route sales conversations there. The lead-capture form remains the
           fallback for hosts with no bridge (standalone dev, Vue-to-Vue
           embeds) or when the bridge call fails. -->
      <v-btn
        v-if="salesChatAvailable"
        color="primary"
        block
        :loading="openingSalesChat"
        prepend-icon="mdi-forum-outline"
        @click="openSalesChat"
      >
        {{ t('sales.speakWithSales') }}
      </v-btn>
      <ContactSalesForm v-else :reason="reason" />
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '../composables/useI18n.js'
const { t } = useI18n()
import { computed, ref } from 'vue'
import { useCreditsStore } from '../stores/credits.js'
import { useParentBridge } from '../composables/useParentBridge.js'
import { useTheme } from '../composables/useTheme.js'
import ContactSalesForm from './sales/ContactSalesForm.vue'
import iconDark from '../assets/icons/rpilot-icon-dark.svg'
import iconLight from '../assets/icons/rpilot-icon-light.svg'

const props = defineProps({
  // Mirrors features.credits. When false the gate is inert — it always renders
  // the default slot and never inspects the store.
  enabled: { type: Boolean, default: false },
})

const creditsStore = useCreditsStore()

// Render chat when: feature off, OR the check has completed and we're not
// gated (which includes the fail-open path, since `gated` stays false on
// error). While enabled && !checked && !gated, fall through to the spinner.
const renderChat = computed(
  () => !props.enabled || (creditsStore.checked && !creditsStore.gated)
)

const reason = computed(() => creditsStore.gateReason)

// Sales-chat handover: opens the parent SPA's Freshchat widget with a
// localized greeting pre-filled (the user reviews and sends it themselves).
// The parent hides the RPilot shell as part of opening the chat.
const parentBridge = useParentBridge()
const salesChatFailed = ref(false)
const openingSalesChat = ref(false)
const salesChatAvailable = computed(
  () => !salesChatFailed.value && parentBridge.isActive && !!parentBridge.supportOpenChatAction
)

async function openSalesChat() {
  openingSalesChat.value = true
  try {
    await parentBridge.call(
      parentBridge.supportOpenChatAction,
      { summary: t('sales.salesChatPrefill') },
      { timeoutMs: 10000 }
    )
  } catch {
    // Host build without the support action, or timeout — degrade to the form.
    salesChatFailed.value = true
  } finally {
    openingSalesChat.value = false
  }
}

const { isDark } = useTheme()
const gateIcon = computed(() => (isDark() ? iconDark : iconLight))
</script>

<style scoped>
.credit-gate {
  flex: 1 1 0;
  min-height: 240px;
  width: 100%;
  overflow-y: auto;
  background: rgb(var(--v-theme-surface));
}
.gate-inner {
  width: 100%;
  max-width: 360px;
}
.gate-icon {
  width: 64px;
  height: 64px;
  display: inline-block;
}
</style>
